import { HistoryField } from "@common/constants/history-source-types";
import { capitalize } from "@common/helpers/string";
import {
    AuditLogChangeType,
    type AuditLogDiff,
    type HistoryDetailsItem,
    ResourceType,
} from "@common/types/auditLog";
import { buildTaskUpdateDetailsRow } from "@components/history/details/task/utils/historyTaskRows";
import {
    formatAuditLogRefValue,
    getHistoryFieldLabel,
} from "@components/history/details/utils/historyDetailsHelpers";
import { isEmptySnapshotValue } from "@components/history/details/utils/isEmptySnapshotValue";
import { formatDateOnly } from "@helpers/date";

/** What one change set a value to, and from what. */
export interface WorkActivityChangeValues {
    fieldLabel: string | null;
    /** Unset when the value was empty, so the line shows only the new one. */
    previous: string | undefined;
    modified: string;
}

/** One change in an expanded activity line. */
export interface WorkActivityChangeLine {
    id: string;
    /** The changed task's title; unset on the work item's own changes, which the pane already names. */
    title: string | undefined;
    /** Set when the title names a task, which it then links to. */
    taskId: string | undefined;
    /** Unset on a created task, which its title already describes. */
    values: WorkActivityChangeValues | undefined;
}

const DATE_FIELDS: ReadonlySet<string> = new Set([
    HistoryField.WORK_START_DATE,
    HistoryField.WORK_DELIVERY_DATE,
]);

/** Work start and Work end read as dates; every other value as the Activity page shows it. */
const formatWorkItemValue = (field: string, ref: AuditLogDiff["before"]) => {
    if (!ref.valueLabel && DATE_FIELDS.has(field)) {
        return formatDateOnly(typeof ref.value === "string" ? ref.value : null);
    }
    return formatAuditLogRefValue(ref);
};

/**
 * The lines behind one audit row: a work item edit gets a line per changed
 * field (Work start, Work end, Preparer…), a task change keeps the Activity
 * page's one line per task. A value set from empty shows only the new value.
 */
export const toWorkActivityChangeLines = (
    log: HistoryDetailsItem,
): WorkActivityChangeLine[] => {
    if (log.resourceType === ResourceType.CLOSE) {
        return log.diff.map((diff) => ({
            id: `${log.id}-${diff.field}`,
            title: undefined,
            taskId: undefined,
            values: {
                fieldLabel: capitalize(getHistoryFieldLabel(diff.field)),
                previous: isEmptySnapshotValue(diff.before)
                    ? undefined
                    : formatWorkItemValue(diff.field, diff.before),
                modified: formatWorkItemValue(diff.field, diff.after),
            },
        }));
    }
    const row = buildTaskUpdateDetailsRow(log);
    const isTask = row.resourceType === ResourceType.TASK;
    const isCreated = log.changeType === AuditLogChangeType.CREATE;
    const isPreviousEmpty =
        isCreated ||
        (log.diff.length > 0 &&
            log.diff.every((diff) => isEmptySnapshotValue(diff.before)));
    return [
        {
            id: row.id,
            title: row.taskTitle,
            taskId:
                isTask && log.changeType !== AuditLogChangeType.DELETE
                    ? row.resourceId
                    : undefined,
            values:
                isTask && isCreated
                    ? undefined
                    : {
                          fieldLabel: row.changedFieldLabel,
                          previous: isPreviousEmpty ? undefined : row.previous,
                          modified: row.modified,
                      },
        },
    ];
};
